const DEFAULT_TIMEOUT_MS = 25_000;

function apiHeaders(): Record<string, string> {
  return {
    Accept: "application/json",
  };
}

function wheelPath(path: string): string {
  if (path.startsWith("/api/") || path.startsWith("/v1/")) return path;
  return `/api/v1/wheel${path}`;
}

function wheelErrorMessage(payload: unknown, status: number): string {
  if (payload && typeof payload === "object") {
    const detail = (payload as { detail?: { message?: string; code?: string } }).detail;
    if (detail?.message) return detail.message;
    const error = (payload as { error?: string }).error;
    if (error) return error;
  }
  return `Request failed (${status})`;
}

export async function wheelGet<T>(
  path: string,
  force = false,
  timeoutMs = DEFAULT_TIMEOUT_MS,
): Promise<T> {
  const normalized = wheelPath(path);
  const headers = apiHeaders();
  if (force) {
    headers["Cache-Control"] = "no-cache";
    headers["X-SFE-Refresh"] = "1";
  }

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(normalized, {
      headers,
      credentials: "same-origin",
      signal: controller.signal,
    });
    if (!res.ok) {
      const contentType = res.headers.get("content-type") ?? "";
      let payload: unknown = null;
      let bodyText = "";
      if (contentType.includes("json")) {
        payload = await res.json().catch(() => null);
      } else {
        bodyText = await res.text().catch(() => "");
      }
      const err = new Error(
        payload != null
          ? wheelErrorMessage(payload, res.status)
          : `${normalized}: ${res.status}${bodyText ? ` — ${bodyText.slice(0, 160)}` : ""}`,
      ) as Error & { status?: number; code?: string };
      err.status = res.status;
      if (payload && typeof payload === "object") {
        const detail = (payload as { detail?: { code?: string } }).detail;
        err.code = detail?.code;
      }
      throw err;
    }
    return res.json() as Promise<T>;
  } catch (err) {
    if (err instanceof Error && err.name === "AbortError") {
      throw new Error(`${normalized}: timed out after ${Math.round(timeoutMs / 1000)}s`);
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
}

export async function wheelMutate<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const normalized = wheelPath(path);
  const headers: Record<string, string> = {
    ...apiHeaders(),
    Accept: "application/json",
  };
  if (body != null) headers["Content-Type"] = "application/json";

  const res = await fetch(normalized, {
    method: method.toUpperCase(),
    headers,
    credentials: "same-origin",
    body: body != null ? JSON.stringify(body) : undefined,
  });

  const contentType = res.headers.get("content-type") ?? "";
  const payload = contentType.includes("json")
    ? await res.json().catch(() => null)
    : null;

  if (!res.ok) {
    const err = new Error(wheelErrorMessage(payload, res.status)) as Error & {
      status?: number;
      code?: string;
    };
    err.status = res.status;
    if (payload && typeof payload === "object") {
      const detail = (payload as { detail?: { code?: string } }).detail;
      err.code = detail?.code;
    }
    throw err;
  }

  return payload as T;
}

export async function proxyGet<T>(path: string): Promise<T> {
  const res = await fetch(path, { headers: apiHeaders(), credentials: "same-origin" });
  if (!res.ok) {
    const payload = await res.json().catch(() => null);
    throw new Error(wheelErrorMessage(payload, res.status));
  }
  return res.json() as Promise<T>;
}

export async function proxyMutate<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method,
    headers: { ...apiHeaders(), Accept: "application/json", ...(body != null ? { "Content-Type": "application/json" } : {}) },
    credentials: "same-origin",
    body: body != null ? JSON.stringify(body) : undefined,
  });
  const payload = await res.json().catch(() => null);
  if (!res.ok) throw new Error(wheelErrorMessage(payload, res.status));
  return payload as T;
}
